/**
 * Состояние авторизации. Единственный источник истины — GET /auth/me:
 * токены в JS недоступны, поэтому «вошёл ли пользователь» выясняется у сервера.
 */
import { createContext, useContext, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api, onSessionLost } from '@/shared/api/client'
import type { AuthResponse, User } from '@/entities/user/types'

const ME_KEY = ['auth', 'me'] as const

export interface LoginInput {
  email: string
  password: string
}

export interface RegisterInput {
  email: string
  password: string
  first_name: string
  last_name?: string
  role: 'student' | 'teacher'
  grade?: number | null
}

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  login: (input: LoginInput) => Promise<AuthResponse>
  register: (input: RegisterInput) => Promise<AuthResponse>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<AuthResponse>('/auth/me')
      } catch (error) {
        // Неавторизованный гость — штатное состояние, а не сбой.
        if (error instanceof ApiError && error.status === 401) return null
        throw error
      }
    },
    retry: false,
    staleTime: 60_000,
  })

  // Когда обновление токена окончательно провалилось, сбрасываем пользователя.
  useEffect(
    () =>
      onSessionLost(() => {
        queryClient.setQueryData(ME_KEY, null)
      }),
    [queryClient],
  )

  // Access-токен живёт 15 минут. Обновляем заметно раньше, чтобы пользователь
  // не встречал 401 посреди работы над заданием.
  useEffect(() => {
    const ttl = me.data?.access_expires_in
    if (!ttl) return
    const delay = Math.max(30, ttl - 120) * 1000
    const timer = setTimeout(() => {
      void api.post('/auth/refresh').catch(() => undefined)
    }, delay)
    return () => clearTimeout(timer)
  }, [me.data?.access_expires_in])

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => api.post<AuthResponse>('/auth/login', input),
    onSuccess: (data) => queryClient.setQueryData(ME_KEY, data),
  })

  const registerMutation = useMutation({
    mutationFn: (input: RegisterInput) => api.post<AuthResponse>('/auth/register', input),
    onSuccess: (data) => queryClient.setQueryData(ME_KEY, data),
  })

  const logoutMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/auth/logout'),
    onSettled: () => {
      queryClient.setQueryData(ME_KEY, null)
      queryClient.clear()
    },
  })

  const value: AuthContextValue = {
    user: me.data?.user ?? null,
    isLoading: me.isPending,
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: async () => {
      await logoutMutation.mutateAsync()
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth требует AuthProvider')
  return ctx
}
